import { VARIANTS } from './displayVariant';

// "How it works" is the page's own plain explanation, built ONLY from the generic convention parameters (marker,
// keyword, occurrence, pattern, answerLength, alphabet) and, for a keyword, from the step codes of the engine
// examples. The raw engine label is never shown. Each paragraph is { id, values } for react-intl; `marker` and
// `keyword` values are raw strings the section renders as inline secret chips.

const P = 'conventionPage.how.';

export const PATTERN_CLASSES = Object.freeze(['DIGIT', 'LOWER', 'UPPER', 'LOWER_OR_DIGIT', 'UPPER_OR_DIGIT']);

// Case only matters for a letter: a digit (or any caseless character) has no upper / lower form.
export const isCasedCharacter = (value) =>
    typeof value === 'string' && value.length > 0 && value.toLowerCase() !== value.toUpperCase();

// The answer can only contain letters when the alphabet is not digits-only.
export const answerHasCase = (parameters) => !!parameters && parameters.alphabet !== 'DIGITS';

// Which case wording applies to a marker: anyCase / exactCase for a letter, none for a digit.
export const markerCaseKind = (parameters) => {
    if (!parameters || !isCasedCharacter(parameters.marker)) return 'plain';
    return parameters.markerCaseSensitive === true ? 'exactCase' : 'anyCase';
};

// Keyword rule, read from the engine examples: a TAKE_PREVIOUS step means "after if uppercase, before if
// lowercase"; only TAKE_NEXT steps mean "always the next one"; no example (yet) means undetermined.
export const keywordRule = (examples) => {
    const items = examples && examples.status === 'ready' && Array.isArray(examples.items) ? examples.items : [];
    const codes = [];
    items.forEach((item) => {
        const steps = item && item.solution && Array.isArray(item.solution.steps) ? item.solution.steps : [];
        steps.forEach((step) => step && codes.push(step.code));
    });
    if (codes.indexOf('TAKE_PREVIOUS') !== -1) return 'caseDirection';
    if (codes.indexOf('TAKE_NEXT') !== -1) return 'next';
    return 'pending';
};

const hasPattern = (parameters) => Array.isArray(parameters.pattern) && parameters.pattern.length > 0;

const markerParagraphs = (parameters) => {
    const count = parameters.answerLength;
    const caseKind = markerCaseKind(parameters);
    if (hasPattern(parameters)) {
        const occurrence = Number.isInteger(parameters.occurrence) && parameters.occurrence > 0 ? parameters.occurrence : 1;
        const known = parameters.pattern.every((cls) => PATTERN_CLASSES.indexOf(cls) !== -1);
        return [
            { id: `${P}pattern.find.${caseKind}`, values: { marker: parameters.marker, occurrence } },
            known
                ? { id: `${P}pattern.take`, values: { classes: parameters.pattern.slice() } }
                : { id: `${P}pattern.takeGeneric`, values: { count } },
            { id: `${P}pattern.note`, values: { count } },
        ];
    }
    return [
        { id: `${P}marker.find.${caseKind}`, values: { marker: parameters.marker } },
        { id: `${P}marker.take`, values: { count } },
    ];
};

const keywordParagraphs = (parameters, examples) => [
    { id: `${P}keyword.letters`, values: { keyword: parameters.keyword } },
    { id: `${P}keyword.rule.${keywordRule(examples)}`, values: {} },
    { id: `${P}keyword.type`, values: { count: parameters.answerLength } },
];

export const explanationOf = (variant, parameters, examples) => {
    const params = parameters || {};
    if (variant === VARIANTS.NOTHING_TO_TYPE) return [{ id: `${P}nothing`, values: {} }];
    if (variant === VARIANTS.COPY) {
        return [{ id: params.alphabet === 'DIGITS' ? `${P}copy.digits` : `${P}copy.characters`, values: { count: params.answerLength } }];
    }
    if (variant === VARIANTS.SECRET) {
        return typeof params.marker === 'string' && params.marker.length > 0
            ? markerParagraphs(params)
            : keywordParagraphs(params, examples);
    }
    return [{ id: `${P}unavailable`, values: {} }];
};

// "At sign-in, the challenge is displayed the same way" only makes sense when there is a challenge to answer.
export const hasChallenge = (variant) => variant === VARIANTS.SECRET || variant === VARIANTS.COPY;
